import { useEffect, useRef } from 'react';
import type { MapRef } from 'react-map-gl';
import type { FilterSpecification } from 'mapbox-gl';
import { parkPinsId } from '../utils/layers';

/**
 * Hook to hide Mapbox default park pins when custom park pins are rendered.
 * This prevents duplicate park pins from showing when AllTrails park pins are displayed.
 *
 * Strategy (based on iOS implementation):
 * Uses static expressions on iconImage that check if a feature has AT_ID.
 * If AT_ID exists (meaning it's an AllTrails park), hide the Mapbox icon.
 * This is more performant than querying features and building dynamic expressions.
 *
 * Layer ID Naming Discrepancy:
 * Currently Different Mapbox styles use different layer ID naming conventions:
 * - AllTrails base map (e.g., "alltrailsOutdoorsV2"): Uses plural "parklabels-*"
 *   - Examples: "parklabels-national-park", "parklabels-national-forest"
 * - Satellite/OSM styles: Uses singular "parklabel-*"
 *   - Examples: "parklabel-national-park", "parklabel-city-state-park"
 */
export default function useHideMapboxParkLayers(map: MapRef | undefined, hasParkPins: boolean) {
  const layerStateRef = useRef<
    Map<
      string,
      {
        originalIconImage?: any;
        originalTextField?: any;
      }
    >
  >(new Map());

  useEffect(() => {
    const mapboxMap = map?.getMap();
    if (!mapboxMap || !map?.getStyle) {
      return;
    }

    const updateLayerExpressions = () => {
      const style = map.getStyle();
      if (!style || !style.layers) {
        return;
      }

      // Find layers that match park label patterns or place-* layers (for OSM/satellite styles)
      // IMPORTANT: Exclude our custom park-pins layer - we only want to modify Mapbox's default layers
      const layersToToggle = style.layers
        .filter(layer => {
          if (layer.id === parkPinsId || layer.type !== 'symbol') {
            return false;
          }
          const layerIdLower = layer.id.toLowerCase();
          // Match park label layers across different map styles
          // Base map uses plural "parklabels-*", satellite uses singular "parklabel-*"
          const parkLabels = [
            'parklabels-national-park',
            'parklabels-national-forest',
            'parklabels-city-state-park copy',
            'parklabels-wilderness',
            'parklabel-national-park',
            'parklabel-national-forest',
            'parklabel-city-state-park',
            'parklabel-wilderness'
          ];
          return parkLabels.includes(layerIdLower);
        })
        .map(layer => layer.id);

      // Update expressions for each layer
      layersToToggle.forEach(layerId => {
        try {
          const layer = mapboxMap.getLayer(layerId);
          if (!layer || layer.type !== 'symbol') {
            return;
          }

          // Store original state if not already stored
          if (!layerStateRef.current.has(layerId)) {
            const originalIconImage = layer.layout?.['icon-image'];
            layerStateRef.current.set(layerId, {
              originalIconImage
            });
          }

          const layerState = layerStateRef.current.get(layerId)!;
          const currentIconImage = layer.layout?.['icon-image'] ?? layerState.originalIconImage ?? '';

          if (hasParkPins) {
            // Use static expressions like iOS: if AT_ID exists, hide it (empty string), else show default
            // This is evaluated per-feature by Mapbox's rendering engine, so it's very performant
            const hideExpression: FilterSpecification = ['case', ['has', 'AT_ID'], '', currentIconImage || ''];

            mapboxMap.setLayoutProperty(layerId, 'icon-image', hideExpression as any);
          } else {
            // Restore original values
            try {
              if (layerState.originalIconImage !== undefined) {
                mapboxMap.setLayoutProperty(layerId, 'icon-image', layerState.originalIconImage);
              }
            } catch {
              // Ignore
            }
            layerStateRef.current.delete(layerId);
          }
        } catch {
          // Layer might not exist or might not support these operations
          // Silently ignore errors
        }
      });
    };

    // Update expressions when style loads (e.g., switching from base to satellite)
    // Use 'style.load' instead of 'styledata' - 'styledata' fires too frequently
    // (on source/layer additions), while 'style.load' only fires when a new style is loaded
    const onStyleLoad = () => {
      layerStateRef.current.clear();
      updateLayerExpressions();
    };

    // Update immediately if style is already loaded
    updateLayerExpressions();

    mapboxMap.on('style.load', onStyleLoad);

    return () => {
      mapboxMap.off('style.load', onStyleLoad);

      // Restore layers on unmount
      const layerStates = new Map(layerStateRef.current);
      layerStates.forEach((layerState, layerId) => {
        try {
          const layer = mapboxMap.getLayer(layerId);
          if (layer) {
            if (layerState.originalIconImage !== undefined) {
              mapboxMap.setLayoutProperty(layerId, 'icon-image', layerState.originalIconImage);
            }
          }
        } catch {
          // Ignore
        }
      });

      // Clear refs after cleanup - safe to do as refs are mutable
      // eslint-disable-next-line react-hooks/exhaustive-deps
      layerStateRef.current.clear();
    };
  }, [map, hasParkPins]);
}
